import { DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  useToast,
} from "@chakra-ui/react";
import { collection, getDocs, query, where } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useData } from "../../Contexts/DataContext";
import { db } from "../../firebase";
// import SignupLinkModal from "./SignupLinkModal";

const AddEditUserModal = ({ isOpen, onClose, user = null }) => {
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userClient, setUserClient] = useState("");
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [userIsDeveloper, setUserIsDeveloper] = useState(false);
  const {
    addDocument,
    updateDocument,
    deleteDocument,
    clients,
    users = [],
  } = useData();



  const toast = useToast();
  const [isSignupModalOpen, setSignupModalOpen] = useState(false);
  const [signupUrl, setSignupUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  console.log("users", users);

  useEffect(() => {
    if (!isOpen) return;

    if (user?.id) {
      setUserName(user.name || "");
      setUserEmail(user.email || "");
      setUserClient(user.clientId || "");
      setUserIsAdmin(user.isAdmin || false);
      setUserIsDeveloper(user.isDev || false);
    } else {
      // reset form for "add new"
      setUserName("");
      setUserEmail("");
      setUserClient("");
      setUserIsAdmin(false);
      setUserIsDeveloper(false);
    }
  }, [user, isOpen]);

  const handleDelete = async (userId) => {
    setSubmitting(true);
    try {
      await deleteDocument("users", userId);
      toast({
        title: "User deleted.",
        description: "The user has been deleted successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error("Error deleting user: ", error);
      toast({
        title: "Error deleting user",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const checkEmailExists = async (email) => {
    const q = query(collection(db, "users"), where("email", "==", email));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const isEditing = !!user?.id;

      // Check for duplicate email only when adding a new user
      if (!isEditing) {
        const emailExists = await checkEmailExists(userEmail);
        if (emailExists) {
          toast({
            title: "Error",
            description: "User with this email already exists.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setSubmitting(false);
          return;
        }
      }

      // Construct the user data object
      const userData = {
        name: userName,
        email: userEmail,
        isAdmin: userIsAdmin,
        isDev: userIsDeveloper,
        clientId: userClient,
      };

      if (isEditing) {
        await updateDocument("users", user.id, userData);
        toast({
          title: "User updated",
          description: "User details have been successfully updated.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        onClose();
      } else {
        await addDocument("users", userData);

        // Generate the signup URL
        const generatedSignupUrl = `${
          window.location.origin
        }/?user=${encodeURIComponent(userEmail)}&signup=true`;
        setSignupUrl(generatedSignupUrl);
        setSignupModalOpen(true);
      }
    } catch (error) {
      console.error("Error saving user: ", error);
      toast({
        title: `Error ${user ? "updating" : "adding"} user`,
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !isSignupModalOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg={"gray.700"} color={"white"}>
            Add New User
          </ModalHeader>
          <ModalCloseButton color={"white"} />

          <ModalBody mt={4}>
            <FormControl id="userName" mb={4} isRequired>
              <FormLabel fontSize={"xs"} fontWeight={"bold"}>
                User Name
              </FormLabel>
              <Input
                fontSize={"xs"}
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
            </FormControl>

            <FormControl id="userEmail" mb={4} isRequired>
              <FormLabel fontSize={"xs"} fontWeight={"bold"}>
                User Email
              </FormLabel>
              <Input
                fontSize={"xs"}
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
              />
            </FormControl>

            <FormControl id="userClient" mb={4} isRequired>
              <FormLabel fontSize={"xs"} fontWeight={"bold"}>
                Client
              </FormLabel>
              <Select
                fontSize={"xs"}
                placeholder="Select client"
                value={userClient}
                onChange={(e) => setUserClient(e.target.value)}
              >
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl mb={4}>
              <Checkbox
                isChecked={userIsDeveloper}
                onChange={(e) => setUserIsDeveloper(e.target.checked)}
                fontSize={"xs"}
              >
                Is Developer?
              </Checkbox>
            </FormControl>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Flex justifyContent={"space-between"} width="100%">
              {user && (
                <Button
                  leftIcon={<DeleteIcon />}
                  fontSize={"xs"}
                  size={"md"}
                  colorScheme="red"
                  mr={3}
                  variant={"outline"}
                  f
                  onClick={() => handleDelete(user.id)}
                >
                  Delete User
                </Button>
              )}
              <Spacer />
              <ButtonGroup>
                <Button
                  fontSize={"xs"}
                  size={"md"}
                  colorScheme="blue"
                  mr={3}
                  onClick={handleSubmit}
                  isDisabled={
                    !userName || !userEmail || !userClient || submitting
                  }
                  isLoading={submitting}
                >
                  {user ? "Update User" : "Add User"}
                </Button>
                <Button
                  fontSize={"xs"}
                  size={"md"}
                  variant="ghost"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Signup Link Modal */}
      {/* <SignupLinkModal
        isOpen={isSignupModalOpen}
        onClose={() => {
          setSignupModalOpen(false); // Close the SignupLinkModal
          onClose(); // Close the AddUserModal
        }}
        signupUrl={signupUrl}
      /> */}
    </>
  );
};

export default AddEditUserModal;
